import { useHotkeys } from 'react-hotkeys-hook'

import { commands } from '~/components/command-menu/commands'
import { useCommandArgs } from '~/components/command-menu/hooks/useCommandArgs'
import {
  CommandActionArgs,
  CommandContextArgs,
  CommandProps,
} from '~/components/command-menu/types'

export const CommandMenuHotkeys = () => {
  const { actionArgs, contextArgs } = useCommandArgs()

  const registrations = commands.flatMap(
    (command, commandIndex) =>
      command.shortcuts?.map((shortcut, shortcutIndex) => ({
        key: `${command.category}-${commandIndex}-${shortcutIndex}`,
        shortcut,
        command,
      })) ?? [],
  )

  const sequenceRegistrations = registrations.filter(({ shortcut }) =>
    shortcut.includes('>'),
  )
  const otherRegistrations = registrations.filter(
    ({ shortcut }) => !shortcut.includes('>'),
  )

  return (
    <>
      {[...sequenceRegistrations, ...otherRegistrations].map(
        ({ key, shortcut, command }) => (
          <HotkeyRegistration
            key={key}
            shortcut={shortcut}
            command={command}
            contextArgs={contextArgs}
            actionArgs={actionArgs}
          />
        ),
      )}
    </>
  )
}

const HotkeyRegistration = ({
  shortcut,
  command,
  contextArgs,
  actionArgs,
}: {
  shortcut: string
  command: CommandProps
  contextArgs: CommandContextArgs
  actionArgs: CommandActionArgs
}) => {
  const isSequence = shortcut.includes('>')

  useHotkeys(shortcut, (keyboardEvent) => {
    const target = keyboardEvent.target
    if (!(target instanceof Element)) return
    const isInCombobox = !!target.closest('[role="combobox"], [role="listbox"]')
    if (isInCombobox) return

    // For sequences, stop propagation immediately to prevent single-key conflicts
    if (isSequence) {
      keyboardEvent.preventDefault()
      keyboardEvent.stopPropagation()
      keyboardEvent.stopImmediatePropagation()
    }

    const isDisabled = command.isDisabled?.(contextArgs) ?? false
    if (isDisabled) return

    if (command.restrictModifiers) {
      if (
        keyboardEvent.metaKey ||
        keyboardEvent.ctrlKey ||
        keyboardEvent.altKey ||
        keyboardEvent.shiftKey
      ) {
        return
      }
    }

    // For non-sequences, stop propagation here
    if (!isSequence) {
      keyboardEvent.preventDefault()
      keyboardEvent.stopPropagation()
      keyboardEvent.stopImmediatePropagation()
    }

    command.onSelect({ ...contextArgs, actions: actionArgs })
  })
  return null
}
